/**
 * GENERATED_ASSET — wear model.
 * Every effect draws from a FIXED candidate list on its own RNG channel and shows the first N, so raising
 * stoneWear/mossCoverage only ADDS marks and never reshuffles which sites/positions are used. Cracks and salt
 * keep identical geometry as wear rises; moss caps keep their sites but grow slightly with coverage.
 * Channels are independent: changing moss never moves a crack, and vice versa.
 */
import type { Ctx } from './ctx';
import type { Layer } from './svgKit';
import { Path } from './pathKit';
import type { Pt } from './pathKit';
import { BASE_Y, SLAB_D } from './layout';

export interface Chip { t: number; w: number; d: number }
const MAX_WEAR = 0.65, MAX_MOSS = 0.6;
const count = (level: number, max: number, n: number) => Math.round((level / max) * n);

export function chipCandidates(ctx: Ctx, channel: string, n: number): Chip[] {
  const r = ctx.rng(channel);
  return Array.from({ length: n }, () => ({ t: r.range(0.06, 0.94), w: r.range(4, 9), d: r.range(1.6, 3.6) }));
}

/** Slab front edge from x0 to x1 along y with the spillway notch and active chips biting downward. */
export function slabFrontLine(ctx: Ctx, k: number): Pt[] {
  const t = ctx.L.tiers[k]!;
  const chips = chipCandidates(ctx, `chips.slab.${k}`, 7).slice(0, count(ctx.p.stoneWear, MAX_WEAR, 7));
  const half = t.notchW / 2;
  const pts: Pt[] = [{ x: t.slabX0, y: t.s }];
  const span = t.slabX1 - t.slabX0;
  const events: Array<{ x0: number; x1: number; pts: Pt[] }> = [];
  events.push({
    x0: t.lipX - half, x1: t.lipX + half,
    pts: [
      { x: t.lipX - half, y: t.s }, { x: t.lipX - half + 4, y: t.s + 2.2 }, { x: t.lipX - 3, y: t.s + t.notchDepth },
      { x: t.lipX + 3, y: t.s + t.notchDepth }, { x: t.lipX + half - 4, y: t.s + 2.2 }, { x: t.lipX + half, y: t.s },
    ],
  });
  for (const c of chips) {
    const xa = t.slabX0 + c.t * span, xb = xa + c.w;
    if (xb > t.slabX1 - 2 || (xb > t.lipX - half - 3 && xa < t.lipX + half + 3)) continue;
    events.push({ x0: xa, x1: xb, pts: [{ x: xa, y: t.s }, { x: xa + c.w * 0.3, y: t.s + c.d }, { x: xb - c.w * 0.25, y: t.s + c.d * 0.55 }, { x: xb, y: t.s }] });
  }
  events.sort((a, b) => a.x0 - b.x0);
  let lastX = t.slabX0;
  for (const e of events) { if (e.x0 < lastX + 1) continue; pts.push(...e.pts); lastX = e.x1; }
  pts.push({ x: t.slabX1, y: t.s });
  return pts;
}

/** Hairline cracks on stone faces. Always inside the listed face rectangles. */
export function paintCracks(ctx: Ctx, L: Layer): void {
  const { L: lay, sw, p, v } = ctx;
  const faces = [
    { x0: lay.basin.x0 + 10, x1: lay.basin.x1 - 10, y0: 300, y1: 314 },
    ...lay.tiers.map((t) => ({ x0: t.x0 + 6, x1: t.x1 - 6, y0: t.edgeBottom + 7, y1: t.base - 4 })),
    { x0: lay.stele.x0 + 5, x1: lay.stele.x1 - 5, y0: lay.stele.bodyTop + 52, y1: lay.stele.baseY - 4 },
  ];
  const r = ctx.rng('cracks');
  const cands = Array.from({ length: 9 }, (_, i) => {
    const f = faces[i % faces.length]!;
    const x = r.range(f.x0, f.x1), y = r.range(f.y0, f.y0 + (f.y1 - f.y0) * 0.45);
    const pts: Pt[] = [{ x, y }];
    let cx = x, cy = y;
    for (let s = 0; s < 3; s++) {
      cx = Math.max(f.x0, Math.min(f.x1, cx + r.range(-5, 5)));
      cy = Math.min(f.y1, cy + r.range(3, (f.y1 - f.y0) / 2.2 + 2));
      pts.push({ x: cx, y: cy });
    }
    return pts;
  });
  const n = Math.round((p.stoneWear / MAX_WEAR) * 9 * v.crackBias);
  cands.slice(0, Math.min(9, n)).forEach((pts) => {
    L.path(new Path().poly(pts, false), { stroke: sw.stone.seam, sw: 1.05, so: 0.7, part: 'crack' });
  });
}

/** Salt crust and tide stain: pale rims near the waterline, scaled by wear. */
export function paintSalt(ctx: Ctx, L: Layer): void {
  const { L: lay, sw, p, v } = ctx;
  const t0 = lay.tiers[0]!;
  const r = ctx.rng('salt');
  const n = Math.min(14, Math.round((p.stoneWear / MAX_WEAR) * 14 * v.saltBias));
  const sites = [
    { x0: t0.x0 + 4, x1: t0.x1 - 4, y: t0.base - 5, h: 3.4 },
    { x0: lay.basin.x0 + 14, x1: lay.basin.x1 - 14, y: 301.5, h: 3 },
    ...lay.tiers.slice(1).map((t) => ({ x0: t.x0 + 4, x1: t.x1 - 4, y: t.base - 3.5, h: 2.6 })),
  ];
  for (let i = 0; i < 14; i++) {
    const s = sites[i % sites.length]!;
    const w = r.range(7, 18), x = r.range(s.x0, Math.max(s.x0 + 0.1, s.x1 - w)), j = r.range(-0.8, 0.8);
    if (i >= n) continue;
    L.smooth([{ x, y: s.y + s.h }, { x: x + w * 0.3, y: s.y + j }, { x: x + w * 0.7, y: s.y + s.h * 0.2 + j }, { x: x + w, y: s.y + s.h }],
      { fill: sw.salt, fo: 0.7, part: 'salt' });
  }
  if (v.stain > 0) {
    L.path(new Path().poly([{ x: t0.x0 + 1, y: t0.base - 7 }, { x: t0.x1 - 1, y: t0.base - 7 }, { x: t0.x1 - 1, y: t0.base - 1 }, { x: t0.x0 + 1, y: t0.base - 1 }]),
      { fill: sw.stone.shadow, fo: v.stain * 1.4, part: 'tide-stain' });
  }
}

interface MossSite { x0: number; x1: number; yFlat: number; dir: 1 | -1; maxH: number; wet: boolean; avoid?: [number, number] }
function mossSites(ctx: Ctx): MossSite[] {
  const { L: lay } = ctx;
  const sites: MossSite[] = [];
  for (const t of lay.tiers) {
    // Slab back edge (hugs the wall above), slab front edge (clear of the spillway notch), wall foot.
    sites.push({ x0: t.slabX0 + 8, x1: t.slabX1 - 8, yFlat: t.s - SLAB_D + 1.2, dir: 1, maxH: 5, wet: false });
    sites.push({ x0: t.slabX0 + 8, x1: t.slabX1 - 8, yFlat: t.s - 0.7, dir: -1, maxH: 3.6, wet: false, avoid: [t.lipX - t.notchW / 2 - 6, t.lipX + t.notchW / 2 + 6] });
    sites.push({ x0: t.x0 + 4, x1: t.x1 - 4, yFlat: t.base - 1.3, dir: -1, maxH: 5, wet: true });
  }
  const b = lay.basin, st = lay.stele;
  sites.push({ x0: b.x0 + 18, x1: b.x1 - 18, yFlat: BASE_Y - 1.6, dir: -1, maxH: 5, wet: true });
  sites.push({ x0: b.x0 + 16, x1: b.x1 - 16, yFlat: b.openY0 - 1.8, dir: -1, maxH: 4.5, wet: false });
  sites.push({ x0: st.x0 + 3, x1: st.x1 - 3, yFlat: st.baseY - 1.2, dir: -1, maxH: 5, wet: true });
  if (!ctx.v.brokenStele) sites.push({ x0: st.capX0 + 8, x1: st.capX1 - 8, yFlat: st.capFront - 0.6, dir: -1, maxH: 4, wet: false });
  return sites;
}

/** Moss cap: straight contact edge, scalloped free edge growing away from it. Always drawn inside its host surface. */
function mossCap(L: Layer, ctx: Ctx, x0: number, x1: number, yFlat: number, dir: 1 | -1, h: number, r: ReturnType<Ctx['rng']>): void {
  const { sw } = ctx;
  const n = Math.max(4, Math.round((x1 - x0) / 3.4));
  const free: Pt[] = [];
  for (let i = 0; i <= n; i++) {
    const u = i / n, taper = Math.pow(Math.sin(Math.PI * u), 0.55);
    free.push({ x: x0 + (x1 - x0) * u, y: yFlat + dir * h * taper * r.range(0.55, 1) });
  }
  free[0] = { x: x0, y: yFlat }; free[n] = { x: x1, y: yFlat };
  const cap = new Path().smooth(free, false).Z();
  L.path(cap, { fill: sw.moss.base, stroke: sw.moss.dark, sw: 0.8, so: 0.5, part: 'moss' });
  const inner = free.map((q) => ({ x: x0 + (q.x - x0) * 0.82 + (x1 - x0) * 0.09 - (q.x - x0) * 0.0, y: yFlat + (q.y - yFlat) * 0.5 }));
  L.path(new Path().smooth(inner, false).Z(), { fill: sw.moss.light, fo: 0.55, part: 'moss-light' });
  if (ctx.detail.minorRepairs) {
    const tx = x0 + (x1 - x0) * r.range(0.3, 0.7);
    L.line(tx, yFlat + dir * h * 0.55, tx + dir * 0.6, yFlat + dir * (h + 1.6), { stroke: sw.moss.dark, sw: 0.9, so: 0.8, part: 'moss-tuft' });
  }
}

export function paintMoss(ctx: Ctx, L: Layer): void {
  const { p, v } = ctx;
  const sites = mossSites(ctx);
  const r = ctx.rng('moss');
  const N = 28;
  const n = Math.min(N, Math.round((p.mossCoverage / MAX_MOSS) * N * v.mossBias));
  const grow = 0.85 + (p.mossCoverage / MAX_MOSS) * 0.5;
  for (let i = 0; i < N; i++) {
    const s = sites[i % sites.length]!;
    const w = r.range(13, 28) * grow, h = Math.min(s.maxH, r.range(3.6, 6) * grow), u = r.next(), pr = r.fork();
    if (i >= n) continue;
    const len = Math.min(w, s.x1 - s.x0), x0 = s.x0 + u * Math.max(0, s.x1 - s.x0 - len);
    if (s.avoid && x0 + len > s.avoid[0] && x0 < s.avoid[1]) continue;
    mossCap(L, ctx, x0, x0 + len, s.yFlat, s.dir, h, pr);
  }
  if (v.hangingMoss) paintHangingMoss(ctx, L);
}

function paintHangingMoss(ctx: Ctx, L: Layer): void {
  const { L: lay, sw, p } = ctx;
  const r = ctx.rng('moss.hang');
  const cand = lay.tiers.flatMap((t) => Array.from({ length: 3 }, () => ({ t, u: r.range(0.1, 0.9), len: r.range(6, 12) })));
  const n = Math.round((p.mossCoverage / MAX_MOSS) * cand.length);
  cand.slice(0, n).forEach(({ t, u, len }) => {
    const x = t.x0 + 3 + u * (t.w - 6);
    if (Math.abs(x - t.lipX) < t.notchW / 2 + 4) return;
    const y0 = t.edgeBottom - 0.5;
    L.path(new Path().M(x - 2.5, y0).Q(x - 2, y0 + len * 0.7, x - 1.2, y0 + len).L(x + 1.2, y0 + len * 0.8).Q(x + 2, y0 + len * 0.5, x + 2.5, y0).Z(),
      { fill: sw.moss.base, stroke: sw.moss.dark, sw: 0.9, so: 0.85, part: 'moss-hang' });
  });
}

/** Faint stone grain ticks (detail tier). */
export function paintGrain(ctx: Ctx, L: Layer): void {
  const n = ctx.detail.grainMarks;
  if (n === 0) return;
  const { L: lay, sw, style } = ctx;
  const r = ctx.rng('grain');
  const faces = [
    { x0: lay.basin.x0 + 8, x1: lay.basin.x1 - 8, y0: 299.5, y1: 314.5 },
    ...lay.tiers.map((t) => ({ x0: t.x0 + 4, x1: t.x1 - 4, y0: t.edgeBottom + 7, y1: t.base - 3 })),
    { x0: lay.stele.x0 + 4, x1: lay.stele.x1 - 4, y0: lay.stele.bodyTop + 54, y1: lay.stele.baseY - 3 },
  ];
  for (const f of faces) {
    for (let i = 0; i < n; i++) {
      const x = r.range(f.x0, f.x1 - 7), y = r.range(f.y0, f.y1), len = r.range(3, 7);
      L.line(x, y, Math.min(f.x1, x + len), y + r.range(-0.4, 0.4), { stroke: sw.stone.seam, sw: style.grainStroke, so: Math.min(0.5, style.grainOpacity * 2.4), part: 'grain' });
    }
  }
}
